import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { persist } from "zustand/middleware";
import {
  MenuCategory,
  MenuCategoryCreate,
  MenuCategoryUpdate,
  MenuItem,
  CategoryWithMenuItems,
} from "@/types/menu";
import { createCategory, deleteCategory, fetchCategories, updateCategory } from "@/services/menu/categoryService";
import { toggleAvailability, deleteMenuItem, updateMenuItem, fetchMenuItemById } from "@/services/menu/menuItem";

export interface Category {
  id: string;
  name: string;
}

// Mock categories data
const mockCategories: Category[] = [
  { id: "1", name: "Appetizers" },
  { id: "2", name: "Main Courses" },
  { id: "3", name: "Desserts" },
  { id: "4", name: "Beverages" },
  { id: "5", name: "Sides" },
];

interface MenuState {
  categories: MenuCategory[];
  categoriesWithItems: CategoryWithMenuItems[];
  menuItems: Record<string, MenuItem[]>; // Map of categoryId to menu items
  isLoading: boolean;
  error: string | null;
  selectedCategoryId: string | null;

  // Actions
  setSelectedCategory: (categoryId: string | null) => void;
  createCategory: (data: MenuCategoryCreate) => Promise<void>;
  updateCategory: (id: string, data: MenuCategoryUpdate) => Promise<void>;
  deleteCategory: (id: string, storeId: string) => Promise<void>;

  // Menu Items Actions
  toggleMenuItemAvailability: (id: string, available: boolean) => Promise<void>;
  deleteMenuItem: (id: string, storeId: string) => Promise<void>;
  getMenuItemsByCategory: (categoryId: string) => MenuItem[];
  fetchMenuItemById: (id: string) => Promise<MenuItem | null>;
  updateMenuItem: (
    id: string,
    updates: {
      name?: string;
      description?: string;
      price?: number;
      available?: boolean;
      image_url?: string | null;
      store_id?: string;
    },
    categoryIds: string[],
    tagIds: string[]
  ) => Promise<MenuItem | null>;

  // Category actions
  addCategory: (name: string) => string;
  getCategory: (id: string) => MenuCategory | undefined;

  // MenuItem actions
  addMenuItem: (item: Omit<MenuItem, "id">) => string;
  getMenuItem: (id: string) => MenuItem | undefined;

  // Utility actions
  clearMenuItems: () => void;
  resetStore: () => void;

  // Clear local storage and reset menu items
  clearMenuCache: () => void;
}

export const useMenuStore = create<MenuState>()(
  persist(
    (set, get) => ({
      categories: [],
      categoriesWithItems: [],
      menuItems: {},
      isLoading: false,
      error: null,
      selectedCategoryId: null,

      setSelectedCategory: (categoryId) => {
        set({ selectedCategoryId: categoryId });
      },



      createCategory: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const newCategory = await createCategory(data);
          set((state) => ({
            categories: [...state.categories, newCategory],
            categoriesWithItems: [
              ...state.categoriesWithItems,
              { ...newCategory, menu_items: [] },
            ],
            isLoading: false,
          }));
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to create category",
            isLoading: false,
          });
        }
      },

      updateCategory: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const updatedCategory = await updateCategory(id, data);
          set((state) => ({
            categories: state.categories.map((category) =>
              category.id === id ? updatedCategory : category
            ),
            categoriesWithItems: state.categoriesWithItems.map((category) =>
              category.id === id
                ? {
                    ...category,
                    ...updatedCategory,
                    menu_items: category.menu_items,
                  }
                : category
            ),
            isLoading: false,
          }));
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to update category",
            isLoading: false,
          });
        }
      },

      deleteCategory: async (id, storeId) => {
        set({ isLoading: true, error: null });
        try {
          await deleteCategory(id, storeId);
          set((state) => ({
            categories: state.categories.filter(
              (category) => category.id !== id
            ),
            categoriesWithItems: state.categoriesWithItems.filter(
              (category) => category.id !== id
            ),
            menuItems: Object.fromEntries(
              Object.entries(state.menuItems).filter(([key]) => key !== id)
            ),
            selectedCategoryId:
              state.selectedCategoryId === id ? null : state.selectedCategoryId,
            isLoading: false,
          }));
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to delete category",
            isLoading: false,
          });
        }
      },



      toggleMenuItemAvailability: async (id, available) => {
        set({ isLoading: true, error: null });
        try {
          const success = await toggleAvailability(id, available);
          if (success) {
            set((state) => ({
              menuItems: Object.fromEntries(
                Object.entries(state.menuItems).map(([categoryId, items]) => [
                  categoryId,
                  items.map((item) =>
                    item.id === id ? { ...item, available } : item
                  ),
                ])
              ),
              isLoading: false,
            }));
          }
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to toggle menu item availability",
            isLoading: false,
          });
        }
      },

      deleteMenuItem: async (id, storeId) => {
        set({ isLoading: true, error: null });
        try {
          const success = await deleteMenuItem(id, storeId);
          if (success) {
            set((state) => ({
              menuItems: Object.fromEntries(
                Object.entries(state.menuItems).map(([categoryId, items]) => [
                  categoryId,
                  items.filter((item) => item.id !== id),
                ])
              ),
              isLoading: false,
            }));
          }
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to delete menu item",
            isLoading: false,
          });
        }
      },

      getMenuItemsByCategory: (categoryId) => {
        return get().menuItems[categoryId] || [];
      },

      // Category actions
      addCategory: (name) => {
        const newId = uuidv4();
        set((state) => ({
          categories: [
            ...state.categories,
            {
              id: newId,
              name,
              store_id: "", // Required by MenuCategory type
              description: "", // Required by MenuCategory type
            },
          ],
        }));
        return newId;
      },

      getCategory: (id) => {
        return get().categories.find((category) => category.id === id);
      },

      // MenuItem actions
      addMenuItem: (item) => {
        const newId = uuidv4();
        set((state) => ({
          menuItems: {
            ...state.menuItems,
            [item.categoryIds[0]]: [
              ...(state.menuItems[item.categoryIds[0]] || []),
              { ...item, id: newId },
            ],
          },
        }));
        return newId;
      },

      getMenuItem: (id) => {
        const allItems = Object.values(get().menuItems).flat();
        return allItems.find((item) => item.id === id);
      },

      fetchMenuItemById: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const item = await fetchMenuItemById(id);
          set({ isLoading: false });
          return item;
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to fetch menu item",
            isLoading: false,
          });
          return null;
        }
      },

      updateMenuItem: async (id, updates, categoryIds, tagIds) => {
        set({ isLoading: true, error: null });
        try {
          const updatedItem = await updateMenuItem(
            id,
            updates,
            categoryIds,
            tagIds
          );
          if (updatedItem) {
            // Update the item only in the relevant categories
            set((state) => ({
              menuItems: Object.fromEntries(
                Object.entries(state.menuItems).map(([categoryId, items]) => [
                  categoryId,
                  // Only update items in categories that contain this item or are in the new categoryIds
                  items.map((item) => (item.id === id ? updatedItem : item)),
                ])
              ),
              isLoading: false,
            }));
          }
          return updatedItem;
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to update menu item",
            isLoading: false,
          });
          return null;
        }
      },

      // Utility actions
      clearMenuItems: () => {
        set({ menuItems: {} });
      },

      resetStore: () => {
        set({
          categories: [],
          categoriesWithItems: [],
          menuItems: {},
          isLoading: false,
          error: null,
          selectedCategoryId: null,
        });
      },

      // Clear local storage and reset menu items
      clearMenuCache: () => {
        try {
          // Clear from local storage
          localStorage.removeItem("Xerven-menu");
          // Reset menu items in store
          set({ menuItems: {} });
        } catch (error) {
          console.error("Error clearing menu cache:", error);
        }
      },
    }),
    {
      name: "Xerven-menu",
      partialize: (state) => ({
        // Only persist categories and selectedCategoryId, not menuItems
        categories: state.categories,
        categoriesWithItems: state.categoriesWithItems,
        selectedCategoryId: state.selectedCategoryId,
      }),
    }
  )
);

// Add global function for easy cache clearing from browser console
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).clearMenuCache = () => {
    const store = useMenuStore.getState();
    store.clearMenuCache();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).debugMenuStore = () => {
    const store = useMenuStore.getState();
    // Debug info available in development only
    if (process.env.NODE_ENV === "development") {
      // Debug info available in development only
    }
  };
}
